import { api } from '@/lib/api/client';
import type { SiteCmsMediaUsage } from '@shared/types/entities';
import type {
  SaveSiteCmsDraftPayload,
  SiteCmsAccessResponse,
  SiteCmsCompetitionCard,
  SiteCmsDocumentResponse,
  SiteCmsDomainResponse,
  SiteCmsDraftResponse,
  SiteCmsImageResponse,
  SiteCmsOfferResult,
  SiteCmsOwnerType,
  SiteCmsPublicationState,
  SiteCmsPublicationSummary,
} from '@shared/types/entities';

export function getSiteCmsAccessApi(ownerType: SiteCmsOwnerType, ownerId: number): Promise<SiteCmsAccessResponse> {
  return api.get<SiteCmsAccessResponse>(`/site-cms/access?ownerType=${ownerType}&ownerId=${ownerId}`);
}

/** Only called by the Super Admin interface. The API checks the role again. */
export function updateSiteCmsOfferApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
  enabled: boolean,
): Promise<SiteCmsOfferResult> {
  const root = ownerType === 'ORGANIZATION' ? 'organizations' : 'clubs';
  return api.patch<SiteCmsOfferResult>(`/${root}/${ownerId}/cms-offer`, { enabled });
}

/** Reads an unpublished draft. Access is checked again by NestJS. */
export function getSiteCmsDraftApi(ownerType: SiteCmsOwnerType, ownerId: number): Promise<SiteCmsDraftResponse> {
  return api.get<SiteCmsDraftResponse>(`/site-cms/draft?ownerType=${ownerType}&ownerId=${ownerId}`);
}

export function saveSiteCmsDraftApi(payload: SaveSiteCmsDraftPayload): Promise<SiteCmsDraftResponse> {
  return api.put<SiteCmsDraftResponse>('/site-cms/draft', payload);
}

/** CMS PDFs are private until a trusted publication explicitly copies them. */
export function listSiteCmsDocumentsApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
): Promise<SiteCmsDocumentResponse[]> {
  return api.get<SiteCmsDocumentResponse[]>(`/site-cms/documents?ownerType=${ownerType}&ownerId=${ownerId}`);
}

export function uploadSiteCmsDocumentApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
  slug: string,
  title: string,
  file: File,
): Promise<SiteCmsDocumentResponse> {
  const form = new FormData();
  form.append('ownerType', ownerType);
  form.append('ownerId', String(ownerId));
  form.append('slug', slug);
  form.append('title', title);
  form.append('file', file);
  return api.postMultipart<SiteCmsDocumentResponse>('/site-cms/documents', form);
}

export function deleteSiteCmsDocumentApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
  documentId: string,
): Promise<{ deleted: true }> {
  return api.delete<{ deleted: true }>(
    `/site-cms/documents/${encodeURIComponent(documentId)}?ownerType=${ownerType}&ownerId=${ownerId}`,
  );
}

export function downloadSiteCmsDocumentApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
  documentId: string,
): Promise<Blob> {
  return api.getBlob(
    `/site-cms/documents/${encodeURIComponent(documentId)}/download?ownerType=${ownerType}&ownerId=${ownerId}`,
  );
}

/** S3-C1: hostname management is reserved for SUPER_ADMIN and does not enable a public site. */
export function getSiteCmsAdminDomainApi(ownerType: SiteCmsOwnerType, ownerId: number): Promise<SiteCmsDomainResponse> {
  return api.get<SiteCmsDomainResponse>(`/site-cms/domain/admin?ownerType=${ownerType}&ownerId=${ownerId}`);
}

export function reserveSiteCmsDomainApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
  hostname: string,
): Promise<SiteCmsDomainResponse> {
  return api.put<SiteCmsDomainResponse>('/site-cms/domain', { ownerType, ownerId, hostname });
}

export function verifySiteCmsDomainApi(ownerType: SiteCmsOwnerType, ownerId: number): Promise<SiteCmsDomainResponse> {
  return api.post<SiteCmsDomainResponse>('/site-cms/domain/verify', { ownerType, ownerId });
}

export function getSiteCmsDomainApi(ownerType: SiteCmsOwnerType, ownerId: number): Promise<SiteCmsDomainResponse> {
  return api.get<SiteCmsDomainResponse>(`/site-cms/domain?ownerType=${ownerType}&ownerId=${ownerId}`);
}

export function getLatestSiteCmsPublicationApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
): Promise<SiteCmsPublicationSummary | null> {
  return api.get<SiteCmsPublicationSummary | null>(
    `/site-cms/publications/latest?ownerType=${ownerType}&ownerId=${ownerId}`,
  );
}

export function prepareSiteCmsPublicationApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
  expectedRevision: number,
): Promise<SiteCmsPublicationSummary> {
  return api.post<SiteCmsPublicationSummary>('/site-cms/publications/prepare', {
    ownerType,
    ownerId,
    expectedRevision,
  });
}

/** S3-C2B — privileged publication operations; never exposed to site visitors. */
export function listSiteCmsAdminPublicationsApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
): Promise<SiteCmsPublicationSummary[]> {
  return api.get<SiteCmsPublicationSummary[]>(`/site-cms/publications/admin?ownerType=${ownerType}&ownerId=${ownerId}`);
}

export function stageSiteCmsPublicationApi(id: string): Promise<SiteCmsPublicationSummary> {
  return api.post<SiteCmsPublicationSummary>(`/site-cms/publications/${encodeURIComponent(id)}/stage`, {});
}

export function activateSiteCmsPublicationApi(id: string): Promise<SiteCmsPublicationSummary> {
  return api.post<SiteCmsPublicationSummary>(`/site-cms/publications/${encodeURIComponent(id)}/activate`, {});
}

export function revokeSiteCmsPublicationApi(ownerType: SiteCmsOwnerType, ownerId: number): Promise<{ revoked: true }> {
  return api.post<{ revoked: true }>('/site-cms/publications/revoke', { ownerType, ownerId });
}

export function listSiteCmsImagesApi(ownerType: SiteCmsOwnerType, ownerId: number): Promise<SiteCmsImageResponse[]> {
  return api.get(`/site-cms/images?ownerType=${ownerType}&ownerId=${ownerId}`);
}
export function getSiteCmsImageDataApi(ownerType: SiteCmsOwnerType, ownerId: number, id: string): Promise<string> {
  return api
    .getBlob(`/site-cms/images/${encodeURIComponent(id)}/content?ownerType=${ownerType}&ownerId=${ownerId}`)
    .then(
      blob =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () =>
            typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('Image illisible'));
          reader.onerror = () => reject(new Error('Image illisible'));
          reader.readAsDataURL(blob);
        }),
    );
}
export function uploadSiteCmsImageApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
  title: string,
  alt: string,
  file: File,
): Promise<SiteCmsImageResponse> {
  const form = new FormData();
  form.append('ownerType', ownerType);
  form.append('ownerId', String(ownerId));
  form.append('title', title);
  form.append('alt', alt);
  form.append('file', file);
  return api.postMultipart('/site-cms/images', form);
}
export function updateSiteCmsImageApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
  id: string,
  title: string,
  alt: string,
): Promise<SiteCmsImageResponse> {
  return api.put(`/site-cms/images/${encodeURIComponent(id)}`, { ownerType, ownerId, title, alt });
}
export function deleteSiteCmsImageApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
  id: string,
): Promise<{ deleted: true }> {
  return api.delete(`/site-cms/images/${encodeURIComponent(id)}?ownerType=${ownerType}&ownerId=${ownerId}`);
}
export function renameSiteCmsDocumentApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
  id: string,
  title: string,
): Promise<SiteCmsDocumentResponse> {
  return api.put(`/site-cms/documents/${encodeURIComponent(id)}`, { ownerType, ownerId, title });
}
export function getSiteCmsCompetitionCardsApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
  selectedIds: number[] = [],
): Promise<SiteCmsCompetitionCard[]> {
  return api.get(
    `/site-cms/competition-cards?ownerType=${ownerType}&ownerId=${ownerId}${selectedIds.length ? `&selectedIds=${encodeURIComponent([...new Set(selectedIds)].join(','))}` : ''}`,
  );
}
export function getSiteCmsPublicationStateApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
): Promise<SiteCmsPublicationState> {
  return api.get(`/site-cms/publications/state?ownerType=${ownerType}&ownerId=${ownerId}`);
}
export function publishSiteCmsOwnedApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
  expectedRevision: number,
): Promise<SiteCmsPublicationSummary> {
  return api.post('/site-cms/publications/publish', { ownerType, ownerId, expectedRevision });
}
export function restoreSiteCmsOwnedApi(
  ownerType: SiteCmsOwnerType,
  ownerId: number,
  id: string,
): Promise<SiteCmsPublicationSummary> {
  return api.post(`/site-cms/publications/${encodeURIComponent(id)}/restore`, { ownerType, ownerId });
}
export function unpublishSiteCmsOwnedApi(ownerType: SiteCmsOwnerType, ownerId: number): Promise<{ revoked: true }> {
  return api.post('/site-cms/publications/unpublish', { ownerType, ownerId });
}

export function getSiteCmsMediaUsageApi(ownerType: SiteCmsOwnerType, ownerId: number): Promise<SiteCmsMediaUsage> {
  return api.get<SiteCmsMediaUsage>(`/site-cms/media-usage?ownerType=${ownerType}&ownerId=${ownerId}`);
}
